import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { SERVICE_OPTIONS, BRAND } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: SERVICE_OPTIONS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Request received. Our panel will contact you shortly.");
      setForm({ name: "", email: "", phone: "", service: SERVICE_OPTIONS[0], message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-ov-bone py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.2em] font-semibold text-ov-green mb-6">
              Start your verification
            </p>
            <h2 className="font-display text-4xl md:text-5xl text-ov-navy leading-tight tracking-tight">
              Verify today, <span className="italic text-ov-green">buy tomorrow</span> with confidence.
            </h2>
            <p className="font-body text-ov-charcoal/70 mt-6 text-lg leading-relaxed">
              Share your property details and our expert panel will get back with a clear verification plan.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={BRAND.whatsappUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp"
                className="inline-flex items-center gap-3 font-body text-ov-navy hover:text-ov-green transition-colors"
              >
                <span className="w-10 h-10 rounded-full bg-ov-green/10 flex items-center justify-center text-ov-green font-semibold">WA</span>
                +91 {BRAND.whatsapp}
              </a>
              <div className="font-body text-sm text-ov-charcoal/50">
                {BRAND.website}
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            {done ? (
              <div
                data-testid="contact-success"
                className="h-full min-h-[300px] flex flex-col items-center justify-center text-center border border-ov-green/30 rounded-3xl bg-white p-12"
              >
                <CheckCircle2 size={56} className="text-ov-green mb-6" />
                <h3 className="font-display text-3xl text-ov-navy">Thank you.</h3>
                <p className="font-body text-ov-charcoal/70 mt-3 max-w-sm">
                  Your request has been received. Our panel will reach out to you shortly.
                </p>
                <button
                  data-testid="contact-reset"
                  onClick={() => setDone(false)}
                  className="mt-8 text-sm font-medium text-ov-green underline underline-offset-4"
                >
                  Submit another enquiry
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="bg-white border border-ov-navy/10 rounded-3xl p-8 md:p-10 space-y-7"
              >
                <div className="grid sm:grid-cols-2 gap-7">
                  <Field label="Full Name" testid="input-name">
                    <input
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                      className="ov-input"
                    />
                  </Field>
                  <Field label="Phone" testid="input-phone">
                    <input
                      data-testid="contact-input-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="Mobile number"
                      className="ov-input"
                    />
                  </Field>
                </div>

                <Field label="Email" testid="input-email">
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className="ov-input"
                  />
                </Field>

                <Field label="Service" testid="input-service">
                  <select
                    data-testid="contact-input-service"
                    value={form.service}
                    onChange={update("service")}
                    className="ov-input bg-transparent"
                  >
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Message" testid="input-message">
                  <textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={3}
                    placeholder="Tell us about the property (optional)"
                    className="ov-input resize-none"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-ov-navy text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-ov-green transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />}
                  {loading ? "Sending..." : "Request Verification"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-body text-xs uppercase tracking-[0.15em] text-ov-charcoal/50 font-semibold">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
